import React, { useState } from 'react';
import {
  User,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Badge,
  Star
} from 'lucide-react';

const DriverSelector = ({ 
  drivers, 
  selectedDriverId, 
  currentDriverId,
  onDriverSelect 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Filter drivers based on search and status
  const filteredDrivers = drivers.filter(driver => {
    const lowerSearch = searchQuery.toLowerCase();
    const driverName = driver.name ? driver.name.toLowerCase() : '';
    const driverEmail = driver.email ? driver.email.toLowerCase() : '';

    const matchesSearch = !searchQuery
      || driverName.includes(lowerSearch)
      || driverEmail.includes(lowerSearch);
    
    const matchesStatus = filterStatus === 'all' || driver.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusDisplay = (status) => {
    const statusMap = {
      'available': { 
        icon: CheckCircle, 
        color: 'text-green-600', 
        bg: 'bg-green-100', 
        label: 'Available' 
      },
      'assigned': { 
        icon: User, 
        color: 'text-blue-600', 
        bg: 'bg-blue-100', 
        label: 'Assigned' 
      },
      'on-leave': { 
        icon: XCircle, 
        color: 'text-red-600', 
        bg: 'bg-red-100', 
        label: 'On Leave' 
      },
      'busy': { 
        icon: Clock, 
        color: 'text-yellow-600', 
        bg: 'bg-yellow-100', 
        label: 'Busy' 
      }
    };
    return statusMap[status] || statusMap['available'];
  };

  const getLicenseColor = (licenseClass) => {
    return licenseClass === 'CDL-A' 
      ? 'bg-purple-100 text-purple-800' 
      : 'bg-orange-100 text-orange-800';
  };

  const getSafetyScoreColor = (score) => {
    if (score >= 95) return 'text-green-600';
    if (score >= 85) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getDriverAssignment = (driver) => {
    if (!driver) {
      return null;
    }

    const vehicleNumber = driver.currentVehicleNumber || driver.currentVehicle || null;
    const assignment = { vehicleNumber, assignedDateLabel: null };

    if (driver.currentVehicleAssignedAt) {
      const assignedDate = new Date(driver.currentVehicleAssignedAt);
      if (!Number.isNaN(assignedDate.getTime())) {
        assignment.assignedDateLabel = assignedDate.toLocaleDateString();
      }
    }

    if (!assignment.vehicleNumber) {
      return null;
    }

    return assignment;
  };

  const getCurrentDriver = () => {
    return drivers.find(driver => driver.id === currentDriverId);
  };

  const currentDriver = getCurrentDriver();
  const currentDriverAssignment = getDriverAssignment(currentDriver);

  return (
    <div className="space-y-4">
      {/* Current Assignment Display */}
      {currentDriverId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-full">
                <User className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-blue-900">Currently Assigned</h4>
                <p className="text-sm text-blue-700">
                  {currentDriver?.name || 'Unknown Driver'}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {currentDriverAssignment
                    ? `Vehicle ${currentDriverAssignment.vehicleNumber}${currentDriverAssignment.assignedDateLabel ? ` • since ${currentDriverAssignment.assignedDateLabel}` : ''}`
                    : 'Vehicle assignment details unavailable'}
                </p>
              </div>
            </div>
            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
              Active
            </span>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search drivers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All Status</option>
          <option value="available">Available</option>
          <option value="assigned">Assigned</option>
          <option value="on-leave">On Leave</option>
        </select>
      </div>

      {/* Driver List */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700">
          Select Driver ({filteredDrivers.length} available)
        </h4>
        
        {filteredDrivers.length === 0 ? (
          <div className="text-center py-8">
            <User className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No drivers found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
            {filteredDrivers.map((driver) => {
              const statusDisplay = getStatusDisplay(driver.status);
              const isSelected = driver.id === selectedDriverId;
              const isCurrent = driver.id === currentDriverId;
              const isDisabled = driver.status === 'on-leave';

              return (
                <button
                  key={driver.id}
                  onClick={() => !isDisabled && onDriverSelect(driver.id)}
                  disabled={isDisabled}
                  className={`
                    text-left p-4 rounded-lg border-2 transition-all
                    ${isSelected 
                      ? 'border-blue-500 bg-blue-50' 
                      : isCurrent
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                    }
                    ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="p-2 bg-gray-100 rounded-full">
                        <User className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <h5 className="text-sm font-medium text-gray-900 truncate">
                            {driver.name}
                          </h5>
                          {isCurrent && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate">{driver.email}</p>
                        
                        <div className="flex items-center space-x-4 mt-2">
                          <div className="flex items-center space-x-1">
                            <Badge className="h-3 w-3 text-gray-400" />
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${getLicenseColor(driver.licenseClass)}`}>
                              {driver.licenseClass}
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-1">
                            <Star className="h-3 w-3 text-gray-400" />
                            <span className={`text-xs font-medium ${getSafetyScoreColor(driver.safetyScore)}`}>
                              {driver.safetyScore}%
                            </span>
                          </div>
                          
                          <div className="text-xs text-gray-500">
                            {driver.experience}y exp
                          </div>
                        </div>

                        {driver.currentVehicle && (
                          <div className="mt-2 text-xs text-blue-600">
                            Currently assigned to: {driver.currentVehicle}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <statusDisplay.icon className={`h-4 w-4 mr-2 ${statusDisplay.color}`} />
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusDisplay.bg} ${statusDisplay.color}`}>
                        {statusDisplay.label}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverSelector;
